// Vigencia de la reserva del equipo (30 min). Vive aparte del paso 03 porque la regla es sutil y
// equivocarse deja al vendedor en un ciclo: avanza al paso 06, el silo rechaza con
// `RESERVATION_EXPIRED` y lo devuelve al 03, que vuelve a creerse apartado.

import type { ReservedLine, WizardSession } from './types';

/** Un apartado dura 30 min: el reloj nunca muestra más. */
const MAX_RESERVATION_MINUTES = 30;

/**
 * ¿La reserva sigue viva?
 *
 * `expiresAt` ausente = el silo no manda fecha: no hay evidencia de vencimiento y darla por muerta
 * apartaría una segunda unidad dejando la primera colgada media hora.
 * `now === null` = el reloj de la pantalla todavía no dio su primer tick: no se sabe, y en la duda
 * se responde que NO — el peor caso de equivocarse aquí es re-apartar de más, no el ciclo sin salida.
 */
export function isReservationLive(expiresAt: string | undefined, now: number | null): boolean {
    if (expiresAt === undefined) return true;
    if (now === null) return false;
    return new Date(expiresAt).getTime() > now;
}

/** ¿La reserva sigue viva en este momento? Para handlers, no para render. */
export function isReservationLiveNow(expiresAt: string | undefined): boolean {
    return isReservationLive(expiresAt, Date.now());
}

/**
 * Minutos que le quedan a la reserva, o `null` si no hay ninguno que pintar.
 *
 * Redondea hacia abajo, con 1 como piso mientras viva y 30 como techo.
 * Devuelve `null` — nunca `0` — cuando ya venció: un `0` en pantalla se lee como «queda algo» y es
 * justo el texto que dejó al vendedor esperando sobre una reserva muerta.
 */
export function reservationMinutesLeft(
    expiresAt: string | undefined,
    now: number | null,
): number | null {
    if (expiresAt === undefined || now === null) return null;
    const remaining = new Date(expiresAt).getTime() - now;
    if (remaining <= 0) return null;
    return Math.min(MAX_RESERVATION_MINUTES, Math.max(1, Math.floor(remaining / 60_000)));
}

/** Línea viva (no RELEASING) que vence primero; su vencimiento es el de todo el apartado. */
export function earliestExpiringLineOf(session: WizardSession): ReservedLine | undefined {
    return (session.reservedLines ?? [])
        .filter((l) => l.status !== 'RELEASING')
        .reduce<
            ReservedLine | undefined
        >((first, l) => (first === undefined || Date.parse(l.expiresAt) < Date.parse(first.expiresAt) ? l : first), undefined);
}
